import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Reveal, SectionLabel } from "./motion";

const CHAPTERS = [
  {
    n: "01",
    title: "Precision Matching",
    body: "We don't flood you with resumes. Every candidate is screened, interviewed and benchmarked against your exact role requirements before they reach your desk.",
  },
  {
    n: "02",
    title: "Speed Without Compromise",
    body: "Average shortlist in 72 hours. Our talent network across technology, finance, healthcare and engineering lets us move faster than traditional recruiters.",
  },
  {
    n: "03",
    title: "Career-First for Candidates",
    body: "Resume writing, interview preparation and career counseling come standard. We place people where they will grow — not just where they fit.",
  },
];

const STATS = [
  { value: 12000, suffix: "+", label: "Successful Placements" },
  { value: 480, suffix: "+", label: "Partner Companies" },
  { value: 15, suffix: "+", label: "Years in Recruitment" },
  { value: 92, suffix: "%", label: "Candidate Retention" },
];

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1600;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setDisplay(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return (
    <span ref={ref} className="font-display font-black text-5xl md:text-6xl tracking-tighter text-[#0A1128]">
      {display.toLocaleString("en-IN")}{suffix}
    </span>
  );
};

export default function WhyChooseUs() {
  return (
    <section id="why-us" className="bg-[#F4F5F7] py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="why-choose-us-section">
      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <SectionLabel number="01" text="Why Choose Us" />
            <Reveal>
              <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-[#0A1128]">
                Recruitment,<br />done<br /><span className="text-[#1D4ED8]">properly.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-[#475569] text-base md:text-lg font-light leading-relaxed max-w-sm">
                FAM Job Consultancy connects ambitious companies with skilled professionals — and gives candidates the guidance to actually land the role.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-8">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.1}>
              <div className="group border-t border-[#0A1128]/10 py-10 md:py-14 flex gap-8 md:gap-16 items-start" data-testid={`manifesto-item-${c.n}`}>
                <span className="font-display font-black text-5xl md:text-7xl tracking-tighter text-outline-dark group-hover:text-[#1D4ED8] group-hover:[-webkit-text-stroke:0px] transition-all duration-500">
                  {c.n}
                </span>
                <div>
                  <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight text-[#0A1128] mb-4">{c.title}</h3>
                  <p className="text-[#475569] font-light leading-relaxed max-w-xl">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#0A1128]/10 border border-[#0A1128]/10 mt-8" data-testid="stats-grid">
            {STATS.map((s) => (
              <div key={s.label} className="bg-white p-8 flex flex-col gap-3" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Counter value={s.value} suffix={s.suffix} />
                <span className="text-xs uppercase tracking-[0.2em] text-[#475569]">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
